import type { AssociateStatus } from '@/features/associate/types';
import { ASSIGNMENT_KIND } from '@/features/associate/types';

import { kioskFetch } from './kioskFetch';

/**
 * The status card, read from the api [KAS-1].
 *
 * ⚠️ **Hand-written where the convention (§ 6) asks for generated types**, for the same reason
 * `identityLookup.ts` records: `npm run api:types` reads `../iss-kiosk-api/openapi.json`, which the backend
 * does not emit yet. The shape mirrors `AssociateStatusResponseModel` field for field.
 */
export type AssociateStatusOutcome =
  /**
   * Every card the candidate has, and never an empty list — nothing to show is `none`.
   *
   * **One entry when they are placed, several when they have applied more than once** [Operator,
   * 2026-09-08]. The api decides which: a placement comes back alone (BR-K6), submissions come back in
   * full. The kiosk renders one card or a list of them and does not re-derive that rule.
   */
  | { readonly kind: 'cards'; readonly cards: readonly AssociateStatus[] }
  /**
   * Nothing to show — a real answer rather than a failure: no placement, and no live submission at ANY venue
   * in the past 30 days.
   *
   * ⭐ **The read covers any venue, 30 days back by `appliedAt`, this venue's submissions first** [03-us-kas-1b;
   * decision 0512 points 3–11, which supersedes 0185's and 0194's venue scope]. So an empty `200` no longer means
   * *"nothing at this door"*: a candidate who applied at another venue in the window gets those cards. That is
   * what lets the status screen say *"No submissions in the past 30 days"* on `none` and ask whether to apply
   * for a new job [03-us-kas-1a Scenario 3; 0511 Q3]. `none` still covers a `404`: the api has no 404 branch
   * now, but a boundary reads what it is sent, not what it expects [03-us-kas-1a Scenario 4].
   *
   * The api deliberately does not say why: a candidate with no assignment and one whose submission is
   * barred get the identical response, because a screen in a public lobby must not distinguish them.
   */
  | { readonly kind: 'none' }
  /**
   * The request never produced an answer. The status screen hands off to a recruiter and raises the entry
   * [03-us-kas-1a Scenario 1; decisions 0503, 0504] — never the question `none` asks.
   */
  | { readonly kind: 'unreachable' };

const REQUEST_TIMEOUT_MS = 8_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

/** Validates at the boundary rather than trusting the api (§ 6). */
function readCard(body: unknown): AssociateStatus | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const raw = body as Record<string, unknown>;

  const kind = raw.kind;
  if (kind !== ASSIGNMENT_KIND.placement && kind !== ASSIGNMENT_KIND.submission) return undefined;

  const strings = ['clientName', 'siteName', 'streetAddress', 'shift', 'date'] as const;
  for (const field of strings) if (typeof raw[field] !== 'string') return undefined;

  return {
    kind,
    clientName: raw.clientName as string,
    siteName: raw.siteName as string,
    streetAddress: raw.streetAddress as string,
    shift: raw.shift as string,
    date: raw.date as string,
    // Optional and additive [ISS-144]: the api that predates it sends no `dateValue`, and a card without one
    // is still a card — the screen falls back to the en-US `date`.
    ...(typeof raw.dateValue === 'string' ? { dateValue: raw.dateValue } : {}),
  };
}

/**
 * Fetches the cards. Never throws — every failure is a value, so the caller's job stays "put the candidate on
 * a working screen" rather than "catch an exception mid-render".
 *
 * ⭐ **The venue is the credential's, and it ORDERS the list rather than scoping it** [03-us-kad-6c; decisions
 * 0204, 0221; 03-us-kas-1b; 0512]. It travelled as `venueId=PILOT_VENUE_ID` on the query string until the PIN
 * screen existed; the api now reads it off the kiosk session cookie `kioskFetch` carries. ⛔ **Naming one on
 * the request is a `400 property venueId should not exist`**, so the parameter is gone rather than made
 * optional. Under KAS-1b the api returns any venue's live submissions from the past 30 days with this venue's
 * first, and the kiosk renders them in the order sent — it never re-sorts.
 *
 * ⚠️ The history is kept because it is the failure this endpoint already had once: the call sent no query
 * string at all, every request 400d, and the whole endpoint read as `unreachable` — which fails open, which
 * was the menu defect the Operator measured on 2026-09-10.
 */
export async function fetchAssociateStatus(candidateId: number): Promise<AssociateStatusOutcome> {
  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}/associate/${String(candidateId)}/status`, {
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (error: unknown) {
    reportNoAnswer(isTimeout(error) ? { reason: 'timeout' } : { reason: 'transport' });
    return { kind: 'unreachable' };
  }

  // Kept although the api no longer has a 404 branch: it answered that way before venue scoping, and a
  // boundary that only understands the current deployment breaks against the previous one.
  if (response.status === 404) return { kind: 'none' };
  // A 401 has already been reported, and routed to the PIN pad, by `kioskFetch` [0245] — once is enough.
  if (response.status === 401) return { kind: 'unreachable' };
  if (!response.ok) {
    reportNoAnswer({ reason: 'status', status: response.status });
    return { kind: 'unreachable' };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch (error: unknown) {
    // The timeout also covers the body: a stalled stream is the network, not a malformed answer.
    if (isTimeout(error)) {
      reportNoAnswer({ reason: 'timeout' });
      return { kind: 'unreachable' };
    }
    return malformed();
  }
  if (typeof body !== 'object' || body === null) return malformed();
  const raw = (body as Record<string, unknown>).cards;
  if (!Array.isArray(raw)) return malformed();

  /*
   * Every entry is validated, and one bad entry fails the whole response rather than being skipped. A
   * partially-read list would show a candidate two of their three applications with nothing saying so,
   * which is worse than the neutral hand-off `unreachable` produces.
   */
  const cards: AssociateStatus[] = [];
  for (const entry of raw) {
    const card = readCard(entry);
    if (!card) return malformed();
    cards.push(card);
  }

  /*
   * **An empty list is `none` — nothing to show here — and never `unreachable`** [decision 0185].
   *
   * It read `unreachable` until 2026-09-10, on the premise that the api 404s on empty and an empty list
   * therefore meant the contract had moved. The premise is gone: an empty `200` is how the api says there
   * is nothing to show (today: nothing at any venue in the past 30 days, KAS-1b). Reporting it as a failure
   * fails OPEN downstream — measured by the Operator in live testing, 2026-09-10 — and on the status screen
   * it would now raise a recruiter's entry for a candidate who has simply applied for nothing lately.
   *
   * `{ kind: 'cards', cards: [] }` is deliberately not a state: the union above promises `cards` is never
   * empty, so every consumer can read the first entry without asking.
   */
  return cards.length > 0 ? { kind: 'cards', cards } : { kind: 'none' };
}

/** Why a read produced no answer — the one field an operator needs to tell the four failures apart. */
type NoAnswer =
  | { readonly reason: 'timeout' | 'transport' | 'malformed' }
  | { readonly reason: 'status'; readonly status: number };

function isTimeout(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'TimeoutError'
  );
}

/** A body or a card the boundary cannot read: said once. */
function malformed(): AssociateStatusOutcome {
  reportNoAnswer({ reason: 'malformed' });
  return { kind: 'unreachable' };
}

/**
 * Says out loud that the status read produced no answer, and says nothing about what follows — the screen
 * decides that, and may never get to (a 401 is `kioskFetch`'s to report; an unmounted screen hands off
 * nothing) — ⛔ **to an OPERATOR, never to the candidate** [03-us-kas-1a Scenario 1; the shape of `handoff.ts`'s reports]. Once per
 * `unreachable` except a 401, never on `none` (an empty answer is an answer). ⛔ **No candidate id**: the path is the route's
 * template, not the url that was read, because this prints on a shared device's console.
 */
function reportNoAnswer(noAnswer: NoAnswer): void {
  console.error({
    msg: 'the status read produced no answer — the screen hands the candidate off',
    alert: 'kiosk_status_read_failed',
    path: '/associate/:id/status',
    ...noAnswer,
  });
}
